import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { useToast } from '../hooks/use-toast';
import { Calendar, Users, FileText, LogOut, ChevronRight, Mail, PartyPopper, MessageSquare } from 'lucide-react';

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [stats, setStats] = useState({ reservations: 0, menus: 0 });
  const [recentReservations, setRecentReservations] = useState([]);
  const [loading, setLoading] = useState(true);

  const getAuthHeader = () => {
    const auth = sessionStorage.getItem('adminAuth');
    if (!auth) {
      navigate('/admin');
      return null;
    }
    return { 'Authorization': `Basic ${auth}` };
  };

  useEffect(() => {
    const fetchData = async () => {
      const headers = getAuthHeader();
      if (!headers) return;

      try {
        const [reservationsRes, menusRes] = await Promise.all([
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reservations`, { headers }),
          fetch(`${process.env.REACT_APP_BACKEND_URL}/api/admin/menus`, { headers })
        ]);

        if (!reservationsRes.ok || !menusRes.ok) {
          throw new Error('Unauthorized');
        }

        const reservations = await reservationsRes.json();
        const menus = await menusRes.json();

        setStats({
          reservations: reservations.length,
          menus: menus.length
        });
        setRecentReservations(reservations.slice(0, 5));
      } catch (error) {
        toast({
          title: "Session expirée",
          description: "Veuillez vous reconnecter",
          variant: "destructive"
        });
        navigate('/admin');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [navigate, toast]);

  const handleLogout = () => {
    sessionStorage.removeItem('adminAuth');
    navigate('/admin');
  };

  // French date formatting
  const formatDateFrench = (dateStr) => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const date = new Date(dateStr + 'T00:00:00');
    const dayName = days[date.getDay()];
    const monthName = months[date.getMonth()];
    return `${dayName} ${date.getDate()} ${monthName} ${date.getFullYear()}`;
  };

  const formatTimeFrench = (timeStr) => {
    return timeStr.replace(':', 'h');
  };

  const getTodayFrench = () => {
    const days = ['Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi'];
    const months = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 
                    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
    const now = new Date();
    const dayName = days[now.getDay()];
    const monthName = months[now.getMonth()];
    return `${dayName} ${now.getDate()} ${monthName}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center space-x-4">
            <img
              src="https://customer-assets.emergentagent.com/job_divonne-dining/artifacts/m1azxgvy_Sceau%20en%20cire%20Rouge.png"
              alt="La Rotonde"
              className="h-10"
            />
            <h1 className="text-xl font-bold text-gray-900">Administration</h1>
          </div>
          <Button variant="outline" onClick={handleLogout} className="flex items-center gap-2">
            <LogOut size={18} />
            Déconnexion
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Réservations</p>
                <p className="text-3xl font-bold text-gray-900">{stats.reservations}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Calendar className="text-blue-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Menus actifs</p>
                <p className="text-3xl font-bold text-gray-900">{stats.menus}</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <FileText className="text-green-600" size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-gray-500 text-sm">Aujourd'hui</p>
                <p className="text-lg font-bold text-gray-900">{getTodayFrench()}</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Users className="text-purple-600" size={24} />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Link to="/admin/cms" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Gérer les Menus</h3>
                <p className="text-gray-600">Modifier le menu de la semaine, la carte du soir, etc.</p>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>

          <Link to="/admin/reservations" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Voir les Réservations</h3>
                <p className="text-gray-600">Consulter et gérer les réservations clients</p>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>

          <Link to="/admin/contacts" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Contacts Newsletter</h3>
                <p className="text-gray-600">Gérer les inscriptions et exporter les contacts</p>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>

          <Link to="/admin/events" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Événements</h3>
                <p className="text-gray-600">Créer et gérer les soirées spéciales</p>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>

          <Link to="/admin/feedback-settings" className="bg-white rounded-lg shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">Emails de Feedback</h3>
                <p className="text-gray-600">Paramétrer les demandes d'avis Google et TripAdvisor</p>
              </div>
              <ChevronRight className="text-gray-400" size={24} />
            </div>
          </Link>
        </div>

        {/* Recent Reservations */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b">
            <h2 className="text-lg font-bold text-gray-900">Dernières Réservations</h2>
          </div>
          <div className="divide-y">
            {recentReservations.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                Aucune réservation pour le moment
              </div>
            ) : (
              recentReservations.map((res) => (
                <div key={res.id} className="p-4 flex items-center justify-between hover:bg-gray-50">
                  <div>
                    <p className="font-medium text-gray-900">{res.name}</p>
                    <p className="text-sm text-gray-500">
                      {res.guests} pers. • {formatDateFrench(res.date)} à {formatTimeFrench(res.time)}
                    </p>
                  </div>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    res.status === 'confirmed' ? 'bg-green-100 text-green-800' :
                    res.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                    'bg-yellow-100 text-yellow-800'
                  }`}>
                    {res.status === 'confirmed' ? 'Confirmé' :
                     res.status === 'cancelled' ? 'Annulé' : 'En attente'}
                  </span>
                </div>
              ))
            )}
          </div>
          {recentReservations.length > 0 && (
            <div className="p-4 border-t">
              <Link to="/admin/reservations" className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                Voir toutes les réservations →
              </Link>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
